/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  clearTokenFromCookie,
  decodeTokenDetails,
  getUserTypeFromToken,
  setTokenInCookie,
} from '../_helpers/cookie'
import { History } from '../_helpers/history'
import { handleMessages } from '../_helpers/utilities'
import customerUserService from '../_services/customerUserService'
import userService from '../_services/userService'
import { RootState } from '../_store/store'
import {
  IDeleteAccountFormData,
  IForgetPasswordFormData,
  INewPasswordData,
  ITokenExpiryCheck,
  IUpdateDetails,
  IUserState,
} from './../_models/userInterface'
import { errorAlert } from './alertSlice'

import { getBusinessDetails, setReverifyModalOpen } from './businessSlice'
import { startLoader, stopLoader } from './loaderSlice'
import { closePricingPage, getPricingLimits } from './pricingSlice'
import { updateSubscriptionState } from './publicPagesSlice'
import { errorToast, successToast } from './toastSlice'
import { translation } from './translationSlice'
import { businessVoyagerConfiguration } from './voyagerSlice'

// import { RESET_STATE } from '../_store/actions'

const initialState: IUserState = {
  // loader: false,
  userData: {},
  userProfileState: {},
  forgotPasswordMailAddress: '',
  userProfileInformation: {},
  activePlan: true,
  active_business_id: null,
  businessActiveStatus: '',
}
// USER DETAILS

export const userDetails = createAsyncThunk(
  '/userDetails',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    dispatch(getProfileInformation())
    try {
      const response = await userService.getUserDetails()
      dispatch(businessVoyagerConfiguration())
      const tokenDetails = decodeTokenDetails()
      setTokenInCookie(response?.data?.token)
      if (response?.data?.preferred_language) {
        dispatch(translation(response?.data?.preferred_language))
      }
      dispatch(updateSubscriptionState(response?.is_subscription_based))
      if (
        response?.data?.type === 'business' ||
        (response?.data?.type === 'super_admin' && tokenDetails?.active_business_id !== null)
      ) {
        if (response?.is_subscription_based) {
          dispatch(getPricingLimits({ activePlan: response?.active_plan }))
        }
        // Added for getting country code for the business
        // Can remove once we integrate international payment gateway
        dispatch(getBusinessDetails())
        // end
      }

      dispatch(stopLoader())

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

// USER PROFILE INFORMATION

export const getProfileInformation = createAsyncThunk(
  '/profileInformation',
  async (_, { dispatch, rejectWithValue }) => {
    const userType = getUserTypeFromToken()
    dispatch(startLoader())
    try {
      let response
      if (userType === 'business' || userType === 'super_admin') {
        response = await userService.getProfileInformation()
      } else {
        response = await customerUserService.getProfileInformation()
      }

      dispatch(stopLoader())

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

// UPDATE DETAILS

export const updateDetails = createAsyncThunk(
  '/updateDetails',
  async (userCredentials: IUpdateDetails, { dispatch, rejectWithValue }) => {
    const userType = getUserTypeFromToken()
    dispatch(startLoader())
    try {
      let response
      if (userType === 'business') {
        response = await userService.updateDetails(userCredentials)
      } else {
        response = await customerUserService.updateDetails(userCredentials)
      }
      if (response?.data?.is_phone_updated === true) {
        dispatch(setReverifyModalOpen(true))
      }
      if (response?.data?.preferred_language) {
        dispatch(translation(response?.data?.preferred_language))
      }
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      dispatch(getProfileInformation())
      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      dispatch(getProfileInformation())
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

// FORGET PASSWORD

export const forgetPassword = createAsyncThunk(
  '/forgetPassword',
  async (userCredentials: IForgetPasswordFormData, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await userService.forgetPassword(userCredentials)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      // dispatch(successToast(['Mail sent successfully']))
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

// DELETE ACCOUNT

export const deleteAccount = createAsyncThunk(
  '/deleteAccount',
  async (userCredentials: IDeleteAccountFormData, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    const userType = getUserTypeFromToken()
    try {
      let response
      if (userType === 'business') {
        response = await userService.deleteAccount(userCredentials)
      } else {
        response = await customerUserService.deleteAccount(userCredentials)
      }
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      clearTokenFromCookie()
      // close the pricing modal if delete account is clicked from pricing card
      dispatch(closePricingPage())
      // need to clear the redux state on account delete
      // currently if we clear the redux the user is navigating to /login and the success toast is also not displayed
      // Code for  Dispatch the reset state action
      // dispatch({ type: RESET_STATE })
      History.navigate('/')
      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

// UPDATE PROFILE IMAGE

export const updateProfileImage = createAsyncThunk(
  '/updateProfileImage',
  async (image: File, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    const userType = getUserTypeFromToken()
    try {
      let response
      if (userType === 'business') {
        response = await userService.updateProfileImage(image)
      } else {
        response = await customerUserService.updateProfileImage(image)
      }

      dispatch(successToast(handleMessages(response.message)))
      dispatch(getProfileInformation())
      dispatch(userDetails())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

// DELETE PROFILE IMAGE

export const deleteProfileImage = createAsyncThunk(
  '/deleteProfileImage',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    const userType = getUserTypeFromToken()
    try {
      let response
      if (userType === 'business') {
        response = await userService.deleteProfileImage()
      } else {
        response = await customerUserService.deleteProfileImage()
      }

      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      dispatch(userDetails())
      dispatch(getProfileInformation())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

// Reset password

export const resetPassword = createAsyncThunk(
  '/resetPassword',
  async (newPasswordData: INewPasswordData, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await userService.resetPassword(newPasswordData)
      dispatch(stopLoader())
      History.navigate('/password-updated')
      return response
    } catch (err: any) {
      dispatch(errorAlert(handleMessages(err.message)))
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

// Token expiry check after clicking Reset password button in mail
export const tokenExpiryCheck = createAsyncThunk(
  '/tokenExpiryCheck',
  async (tokenDetails: ITokenExpiryCheck, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await userService.tokenExpiryCheck(tokenDetails)

      if (response?.status === 'success') {
        const { email, token, locale } = tokenDetails
        const encodedEmail = encodeURIComponent(email) // Encode email
        History.push(`/reset-password-page?email=${encodedEmail}&token=${token}&locale=${locale}`)
      }
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      History.navigate('/link-expired')

      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)
export const userPermissionDetails = createAsyncThunk(
  '/userPermissionDetails',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())

    try {
      const response = await userService.getUserPermissions()
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateForgotPasswordMailAddress: (state, data) => {
      state.forgotPasswordMailAddress = data.payload
    },
    updateUserDetails: (state, data) => {
      state.userData = data.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // User Details
      .addCase(userDetails.fulfilled, (state, action) => {
        state.userData = action.payload.data
        state.activePlan = action?.payload?.active_plan
        const tokenDetails = decodeTokenDetails()
        state.active_business_id = tokenDetails?.active_business_id ?? null
        state.businessActiveStatus = action?.payload?.data?.business_status
      })
      .addCase(userDetails.rejected, (state, action) => {
        const responsePayload: any = action.payload
        if (
          responsePayload &&
          responsePayload.data &&
          responsePayload.data.last_subscription_end_date
        ) {
          state.userData.last_subscription_end_date =
            responsePayload.data.last_subscription_end_date
        }
        state.activePlan = initialState.activePlan
      })

      .addCase(getProfileInformation.fulfilled, (state, action) => {
        state.userProfileInformation = action.payload.data
      })
      .addCase(userPermissionDetails.fulfilled, (state, action) => {
        state.userData.activePermissions = action?.payload?.data?.activePermissions
        state.activePlan = action?.payload?.data?.active_plan
      })
  },
})
export const { updateForgotPasswordMailAddress, updateUserDetails } = userSlice.actions

export const userState = (state: RootState) => state.user
const userReducer = userSlice.reducer
export default userReducer
